import React from 'react';
import { formatDateLong, formatDateTime } from '../../utils/date';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { H1, H2, Body } from '../ui/Typography';
import { Spinner } from '../ui/Loading';
import { useModal } from '../../stores/uiStore';
import { useAssessment } from '../../hooks/api';
import { useHasPermission } from '../../hooks/useHasPermission';

interface AssessmentDetailModalProps {
  onEdit?: (assessmentId: string) => void;
  onDelete?: (assessmentId: string) => void;
  onPublish?: (assessmentId: string) => void;
  onUnpublish?: (assessmentId: string) => void;
}

const AssessmentDetailModal: React.FC<AssessmentDetailModalProps> = ({
  onEdit,
  onDelete,
  onPublish,
  onUnpublish,
}) => {
  // Matches the modal id used in Assessments.tsx (`detailModal`). The
  // legacy id was 'view-assessment' which the page never opened, so the
  // detail flow had been broken for any caller -- dashboard rows, list
  // rows, search results -- silently. Aligning with the page id matches
  // the Observation modal's pattern (`observation-detail`).
  const modal = useModal('assessment-detail');
  const assessmentId = modal.data?.assessmentId;
  
  const { data: assessment, isLoading } = useAssessment(
    assessmentId,
    modal.isOpen && !!assessmentId
  );
  
  const canEditAssessment = useHasPermission('assessment.update');
  const canDeleteAssessment = useHasPermission('assessment.delete');
  const canPublishAssessment = useHasPermission('assessment.publish');

  const handleClose = () => {
    modal.close();
  };

  const getScoreBadgeVariant = (score: number | undefined): 'success' | 'warning' | 'error' | 'secondary' => {
    if (!score || !assessment?.assessment_scale) return 'secondary';
    
    const scale = assessment.assessment_scale;
    if (!scale.min_value || !scale.max_value) return 'secondary';
    
    const percentage = ((score - scale.min_value) / (scale.max_value - scale.min_value)) * 100;
    
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    if (percentage >= 40) return 'error';
    return 'secondary';
  };

  const getScaleDescriptors = () => {
    if (!assessment?.assessment_scale?.scale_descriptors) return null;
    
    try {
      if (typeof assessment.assessment_scale.scale_descriptors === 'string') {
        return JSON.parse(assessment.assessment_scale.scale_descriptors);
      }
      return assessment.assessment_scale.scale_descriptors;
    } catch {
      return null;
    }
  };

  const getScoreDescription = (score: number | undefined) => {
    if (!score || !assessment?.assessment_scale) return null;
    
    const descriptors = getScaleDescriptors();
    if (!descriptors) return null;
    
    // For numeric scales with descriptors
    const scoreKey = score.toString();
    if (descriptors[scoreKey]) {
      return descriptors[scoreKey];
    }
    
    // For descriptive scales
    return Object.entries(descriptors).find(([key, value]) => value === score)?.[0] || null;
  };

  if (isLoading) {
    return (
      <Modal
        isOpen={modal.isOpen}
        onClose={handleClose}
        size="lg"
      >
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Modal>
    );
  }
  
  if (!assessment) {
    return (
      <Modal
        isOpen={modal.isOpen}
        onClose={handleClose}
        size="lg"
      >
        <div className="p-6 text-center">
          <p className="text-gray-500">Assessment not found</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={modal.isOpen}
      onClose={handleClose}
      title="Assessment Details"
      size="lg"
    >
      <div className="max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <H1 className="text-2xl font-bold text-gray-900 mb-2">
                {assessment.child_name ||
                  (assessment.child
                    ? `${assessment.child.name} ${assessment.child.surname}`
                    : 'Unknown Child')}
              </H1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span>{formatDateLong(assessment.assessment_date)}</span>
                {assessment.is_published && (
                  <Badge variant="success" size="sm">Published</Badge>
                )}
                {!assessment.is_published && (
                  <Badge variant="secondary" size="sm">Draft</Badge>
                )}
              </div>
            </div>
            {assessment.score !== undefined && (
              <div className="text-center">
                <Badge variant={getScoreBadgeVariant(assessment.score)} size="lg">
                  Score: {assessment.score}
                  {assessment.assessment_scale && (
                    <span className="text-xs opacity-75">
                      /{assessment.assessment_scale.max_value}
                    </span>
                  )}
                </Badge>
                {getScoreDescription(assessment.score) && (
                  <div className="mt-1 text-xs text-gray-600">
                    {getScoreDescription(assessment.score)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Learning Area & Outcome */}
        <div className="mb-6">
          <H2 className="text-sm font-medium text-gray-700 mb-3">Learning Details</H2>
          <div className="space-y-3">
            {assessment.learning_area && (
              <div>
                <Body className="text-sm text-gray-600">Learning Area</Body>
                <Body className="font-medium text-gray-900">
                  {assessment.learning_area.name}
                  {assessment.learning_area.code && (
                    <span className="text-gray-500 ml-2">({assessment.learning_area.code})</span>
                  )}
                </Body>
              </div>
            )}
            
            {assessment.learning_outcome && (
              <div>
                <Body className="text-sm text-gray-600">Learning Outcome</Body>
                <Body className="font-medium text-gray-900">
                  {assessment.learning_outcome.description}
                  {assessment.learning_outcome.code && (
                    <span className="text-gray-500 ml-2">({assessment.learning_outcome.code})</span>
                  )}
                </Body>
              </div>
            )}
          </div>
        </div>

        {/* Assessment Scale */}
        {assessment.assessment_scale && (
          <div className="mb-6">
            <H2 className="text-sm font-medium text-gray-700 mb-3">Assessment Scale</H2>
            <div className="bg-gray-50 rounded-lg p-4">
              <Body className="font-medium text-gray-900">
                {assessment.assessment_scale.name}
              </Body>
              {assessment.assessment_scale.scale_type && (
                <Body className="text-sm text-gray-600">
                  Type: {assessment.assessment_scale.scale_type}
                </Body>
              )}
              {assessment.assessment_scale.min_value !== undefined && assessment.assessment_scale.max_value !== undefined && (
                <Body className="text-sm text-gray-600">
                  Range: {assessment.assessment_scale.min_value} - {assessment.assessment_scale.max_value}
                </Body>
              )}
              
              {/* Scale Descriptors */}
              {getScaleDescriptors() && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <Body className="text-sm font-medium text-gray-700 mb-2">Scale Guide:</Body>
                  <div className="space-y-1">
                    {Object.entries(getScaleDescriptors()).map(([key, value]) => (
                      <Body key={key} className="text-sm text-gray-600">
                        <span className="font-medium">{key}:</span> {value as string}
                      </Body>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {assessment.notes && (
          <div className="mb-6">
            <H2 className="text-sm font-medium text-gray-700 mb-3">Notes</H2>
            <div className="bg-gray-50 rounded-lg p-4">
              <Body className="text-gray-700 whitespace-pre-wrap">{assessment.notes}</Body>
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="mb-6">
          <H2 className="text-sm font-medium text-gray-700 mb-3">Details</H2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Body className="text-gray-600">Created:</Body>
            <Body className="text-gray-900">
              {formatDateTime(assessment.created_at)}
            </Body>
            
            <Body className="text-gray-600">Last updated:</Body>
            <Body className="text-gray-900">
              {formatDateTime(assessment.updated_at)}
            </Body>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-6 border-t">
          {canEditAssessment && onEdit && (
            <Button
              variant="secondary"
              onClick={() => {
                onEdit(assessment.id);
                handleClose();
              }}
            >
              Edit Assessment
            </Button>
          )}
          
          {canPublishAssessment && !assessment.is_published && onPublish && (
            <Button
              variant="primary"
              onClick={() => {
                onPublish(assessment.id);
                handleClose();
              }}
            >
              Publish Assessment
            </Button>
          )}
          
          {canPublishAssessment && assessment.is_published && onUnpublish && (
            <Button
              variant="secondary"
              onClick={() => {
                onUnpublish(assessment.id);
                handleClose();
              }}
            >
              Unpublish Assessment
            </Button>
          )}
          
          {canDeleteAssessment && onDelete && (
            <Button
              variant="secondary"
              className="text-red-600 hover:text-red-700"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this assessment?')) {
                  onDelete(assessment.id);
                  handleClose();
                }
              }}
            >
              Delete Assessment
            </Button>
          )}
          
          <div className="ml-auto">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AssessmentDetailModal;
